// Export Data Service - Aggregates data for reports
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface ExportDataFilters {
  dateRange: 'today' | 'week' | 'month' | '3months' | '6months' | 'custom';
  customStartDate?: Date;
  customEndDate?: Date;
  includePatients?: boolean;
  includeRevenue?: boolean;
  includeDue?: boolean;
  includeAppointments?: boolean;
  includeAppointmentRevenue?: boolean;
  includeRoomOccupancy?: boolean;
  includeDischarges?: boolean;
}

export interface PatientData {
  id: string;
  name: string;
  phone: string;
  patientType: 'IP' | 'OP';
  registrationDate: string;
  totalPaid: number;
  totalDue: number;
  status: string;
  age?: number;
  gender?: string;
  address?: string;
  bloodGroup?: string;
  roomNumber?: string;
  bedNumber?: string;
}

export interface AppointmentData {
  id: string;
  patientName: string;
  patientPhone?: string;
  doctorName: string;
  department?: string;
  serviceName: string;
  date: string;
  time: string;
  amount: number;
  status: string;
  appointmentType?: string;
  paymentStatus?: string;
}

export interface PaymentData {
  id: string;
  patientId: string;
  patientName: string;
  amount: number;
  type: string;
  paymentMethod: string;
  date: string;
  status: string;
  transactionId?: string;
  razorpayOrderId?: string;
  receiptNumber?: string;
  notes?: string;
}

export interface DischargeData {
  id: string;
  patientName: string;
  admissionDate: string;
  dischargeDate: string;
  totalDays: number;
  totalAmountSpent: number;
}

export interface ExportReportData {
  // Summary Data
  totalPatients: number;
  totalRevenue: number;
  totalDue: number;
  totalAppointments: number;
  appointmentRevenue: number;
  roomOccupancyRate: number;
  totalDischarges: number;
  
  // Detailed Lists
  patients: PatientData[];
  appointments: AppointmentData[];
  payments: PaymentData[];
  discharges: DischargeData[];
  
  // Date Range Info
  startDate: Date;
  endDate: Date;
  generatedAt: Date;
}

export class ExportDataService {
  // Calculate date range based on filter
  static getDateRange(filter: ExportDataFilters): { start: Date; end: Date } {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    let start = new Date();

    switch (filter.dateRange) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        break;
      case 'week':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        start.setHours(0, 0, 0, 0);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        break;
      case '3months':
        start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate(), 0, 0, 0, 0);
        break;
      case '6months':
        start = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate(), 0, 0, 0, 0);
        break;
      case 'custom':
        if (filter.customStartDate && filter.customEndDate) {
          start = new Date(filter.customStartDate);
          start.setHours(0, 0, 0, 0);
          const customEnd = new Date(filter.customEndDate);
          customEnd.setHours(23, 59, 59, 999);
          return { start, end: customEnd };
        }
        break;
    }

    console.log('📅 Calculated Date Range:', {
      filter: filter.dateRange,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      startLocal: start.toLocaleDateString('en-GB'),
      endLocal: end.toLocaleDateString('en-GB')
    });

    return { start, end };
  }

  // Fetch patients data
  static async fetchPatientsData(startDate: Date, endDate: Date): Promise<PatientData[]> {
    try {
      console.log('📊 Fetching patients data...', { startDate, endDate });
      
      const patientsRef = collection(db, 'patients');
      const patientsSnapshot = await getDocs(patientsRef);
      
      const patients: PatientData[] = [];
      
      patientsSnapshot.forEach((doc) => {
        const data = doc.data();
        const regDate = data.registrationDate || data.createdAt || data.admissionDate;
        
        if (regDate) {
          const patientDate = typeof regDate === 'string' 
            ? new Date(regDate) 
            : regDate.toDate ? regDate.toDate() : new Date(regDate);
          
          if (patientDate >= startDate && patientDate <= endDate) {
            patients.push({
              id: doc.id,
              name: data.name || data.patientName || 'N/A',
              phone: data.phone || data.phoneNumber || data.contactNumber || 'N/A',
              patientType: data.patientType || 'OP',
              registrationDate: patientDate.toLocaleDateString('en-GB'),
              totalPaid: 0, // Will be calculated from payments
              totalDue: 0, // Will be calculated from payments
              status: data.statusText || data.status || 'Active',
              age: data.age || undefined,
              gender: data.gender || undefined,
              address: data.address || undefined,
              bloodGroup: data.bloodGroup || undefined,
              roomNumber: data.roomNumber || undefined,
              bedNumber: data.bedNumber || undefined
            });
          }
        }
      });
      
      console.log(`✅ Fetched ${patients.length} patients`);
      patients.forEach(p => {
        console.log(`  - Patient: ${p.name}, ID: ${p.id}, Phone: ${p.phone}`);
      });
      return patients;
    } catch (error) {
      console.error('❌ Error fetching patients:', error);
      return [];
    }
  }

  // Fetch appointments data
  static async fetchAppointmentsData(startDate: Date, endDate: Date): Promise<AppointmentData[]> {
    try {
      const fetchTime = new Date().toISOString();
      console.log('='.repeat(80));
      console.log(`📊 FETCHING APPOINTMENTS DATA - ${fetchTime}`);
      console.log('='.repeat(80));
      console.log('📅 Date range filter:', { 
        start: startDate.toISOString(),
        startLocal: startDate.toLocaleDateString('en-GB'), 
        end: endDate.toISOString(),
        endLocal: endDate.toLocaleDateString('en-GB') 
      });
      
      const appointmentsRef = collection(db, 'appointments');
      const appointmentsSnapshot = await getDocs(appointmentsRef);
      
      console.log(`📦 Total documents in appointments collection: ${appointmentsSnapshot.size}`);
      
      const appointments: AppointmentData[] = [];
      let totalInDB = 0;
      let skippedCount = 0;
      
      appointmentsSnapshot.forEach((doc) => {
        const data = doc.data();
        totalInDB++;
        
        console.log(`\n📄 Processing appointment ${totalInDB}:`, {
          id: doc.id,
          patientName: data.patientName,
          rawDate: data.date,
          rawAppointmentDate: data.appointmentDate,
          rawCreatedAt: data.createdAt,
          amount: data.amount,
          fee: data.fee,
          status: data.status
        });
        
        // Try multiple date fields and formats
        let apptDate: Date | null = null;
        const dateValue = data.date || data.appointmentDate || data.createdAt;
        
        if (dateValue) {
          if (typeof dateValue === 'string') {
            apptDate = new Date(dateValue);
          } else if (dateValue.toDate) {
            // Firebase Timestamp
            apptDate = dateValue.toDate();
          } else if (dateValue.seconds) {
            // Firebase Timestamp object
            apptDate = new Date(dateValue.seconds * 1000);
          } else {
            apptDate = new Date(dateValue);
          }
        }
        
        console.log(`   Parsed date:`, {
          apptDate: apptDate ? apptDate.toISOString() : 'null',
          apptDateLocal: apptDate ? apptDate.toLocaleDateString('en-GB') : 'null',
          isValid: apptDate && !isNaN(apptDate.getTime()),
          withinRange: apptDate ? (apptDate >= startDate && apptDate <= endDate) : false
        });
        
        if (apptDate && !isNaN(apptDate.getTime())) {
          // Check if within date range
          if (apptDate >= startDate && apptDate <= endDate) {
            // Parse amount from multiple possible fields
            const amount = Number(data.amount || data.fee || data.consultationFee || data.totalAmount || 0);
            
            const appointmentData = {
              id: doc.id,
              patientName: data.patientName || data.name || 'N/A',
              patientPhone: data.phone || data.patientPhone || data.phoneNumber || undefined,
              doctorName: data.doctorName || data.doctor || 'N/A',
              department: data.department || data.specialization || data.specialty || undefined,
              serviceName: data.serviceName || data.service || data.serviceType || 'Consultation',
              date: apptDate.toLocaleDateString('en-GB'),
              time: data.time || data.appointmentTime || 'N/A',
              amount: amount,
              status: data.status || 'Pending',
              appointmentType: data.appointmentType || data.type || undefined,
              paymentStatus: data.paymentStatus || undefined
            };
            
            appointments.push(appointmentData);
            
            console.log(`   ✅ INCLUDED:`, appointmentData);
          } else {
            skippedCount++;
            console.log(`   ⏭️ SKIPPED (outside date range)`);
          }
        } else {
          skippedCount++;
          console.log(`   ⚠️ SKIPPED (invalid date)`);
        }
      });
      
      console.log('='.repeat(80));
      console.log(`✅ APPOINTMENTS FETCH COMPLETE - ${fetchTime}`);
      console.log(`   Total in database: ${totalInDB}`);
      console.log(`   Matching date range: ${appointments.length}`);
      console.log(`   Skipped: ${skippedCount}`);
      console.log('='.repeat(80));
      
      return appointments;
    } catch (error) {
      console.error('❌ Error fetching appointments:', error);
      return [];
    }
  }

  // Fetch payments data
  static async fetchPaymentsData(startDate: Date, endDate: Date): Promise<PaymentData[]> {
    try {
      console.log('📊 Fetching payments data...');
      
      const paymentsRef = collection(db, 'payments');
      const paymentsSnapshot = await getDocs(paymentsRef);
      
      const allPayments: any[] = [];
      
      // First, collect all payments from Firebase
      paymentsSnapshot.forEach((doc) => {
        const data = doc.data();
        allPayments.push({
          id: doc.id,
          patientId: data.patientId || data.userId || '',
          patientName: data.patientName || data.name || 'N/A',
          amount: Number(data.amount || data.totalAmount || 0),
          paidAmount: Number(data.paidAmount || 0),
          dueAmount: Number(data.dueAmount || 0),
          totalAmount: Number(data.totalAmount || 0),
          type: data.type || data.paymentType || 'Service',
          paymentMethod: data.paymentMethod || data.method || 'Cash',
          date: data.date || data.createdAt || data.timestamp,
          lastPaymentDate: data.lastPaymentDate || data.date || data.createdAt,
          registrationDate: data.registrationDate || data.createdAt,
          status: data.status || data.paymentStatus || 'Completed',
          transactionId: data.transactionId || data.txnId || undefined,
          razorpayOrderId: data.razorpayOrderId || data.orderId || undefined,
          receiptNumber: data.receiptNumber || data.receiptNo || undefined,
          notes: data.notes || data.description || undefined,
          rawData: data
        });
      });
      
      // Group payments by patientId to get latest payment per patient
      const latestPaymentsByPatient = new Map<string, any>();
      
      allPayments.forEach(payment => {
        if (!payment.patientId) return;
        
        const existing = latestPaymentsByPatient.get(payment.patientId);
        if (!existing) {
          latestPaymentsByPatient.set(payment.patientId, payment);
        } else {
          // Keep the latest payment (newest date)
          const existingDate = new Date(existing.lastPaymentDate || existing.registrationDate || 0).getTime();
          const currentDate = new Date(payment.lastPaymentDate || payment.registrationDate || 0).getTime();
          
          if (currentDate > existingDate) {
            latestPaymentsByPatient.set(payment.patientId, payment);
          }
        }
      });
      
      console.log(`📊 Total payments in DB: ${allPayments.length}, Unique patients: ${latestPaymentsByPatient.size}`);
      
      // Filter by date range using the latest payment per patient
      const payments: PaymentData[] = [];
      
      latestPaymentsByPatient.forEach((payment) => {
        const payDate = payment.lastPaymentDate 
          ? (typeof payment.lastPaymentDate === 'string' ? new Date(payment.lastPaymentDate) : payment.lastPaymentDate.toDate ? payment.lastPaymentDate.toDate() : new Date(payment.lastPaymentDate))
          : new Date();
        
        if (payDate >= startDate && payDate <= endDate) {
          payments.push({
            id: payment.id,
            patientId: payment.patientId,
            patientName: payment.patientName,
            amount: payment.paidAmount || payment.amount, // Use paidAmount (cumulative paid)
            type: payment.type,
            paymentMethod: payment.paymentMethod,
            date: payDate.toLocaleDateString('en-GB'),
            status: payment.status,
            transactionId: payment.transactionId,
            razorpayOrderId: payment.razorpayOrderId,
            receiptNumber: payment.receiptNumber,
            notes: payment.notes
          });
        }
      });
      
      console.log(`✅ Fetched ${payments.length} latest payments (within date range)`);
      payments.forEach(p => {
        console.log(`  - Payment: ${p.patientName} (ID: ${p.patientId}), Amount: ${p.amount}, Status: ${p.status}`);
      });
      return payments;
    } catch (error) {
      console.error('❌ Error fetching payments:', error);
      return [];
    }
  }

  // Fetch discharge records
  static async fetchDischargesData(startDate: Date, endDate: Date): Promise<DischargeData[]> {
    try {
      console.log('📊 Fetching discharges data...');
      
      const dischargesRef = collection(db, 'discharges');
      const dischargesSnapshot = await getDocs(dischargesRef);
      
      const discharges: DischargeData[] = [];
      
      dischargesSnapshot.forEach((doc) => {
        const data = doc.data();
        const dischargeDate = data.dischargeDate ? new Date(data.dischargeDate) : null;
        
        if (dischargeDate && dischargeDate >= startDate && dischargeDate <= endDate) {
          discharges.push({
            id: doc.id,
            patientName: data.patientName || 'N/A',
            admissionDate: data.admissionDate ? new Date(data.admissionDate).toLocaleDateString('en-GB') : 'N/A',
            dischargeDate: dischargeDate.toLocaleDateString('en-GB'),
            totalDays: data.totalDays || 0,
            totalAmountSpent: Number(data.totalAmountSpent || 0)
          });
        }
      });
      
      console.log(`✅ Fetched ${discharges.length} discharges`);
      return discharges;
    } catch (error) {
      console.error('❌ Error fetching discharges:', error);
      return [];
    }
  }

  // Fetch room occupancy rate
  static async fetchRoomOccupancyRate(): Promise<number> {
    try {
      const roomsRef = collection(db, 'rooms');
      const roomsSnapshot = await getDocs(roomsRef);
      
      let totalBeds = 0;
      let occupiedBeds = 0;
      
      roomsSnapshot.forEach((doc) => {
        const data = doc.data();
        const capacity = data.capacity || data.totalBeds || 0;
        const occupied = data.currentOccupancy || 0;
        
        totalBeds += capacity;
        occupiedBeds += occupied;
      });
      
      return totalBeds > 0 ? Math.round((occupiedBeds / totalBeds) * 100) : 0;
    } catch (error) {
      console.error('❌ Error fetching room occupancy:', error);
      return 0;
    }
  }

  // Main function to aggregate all export data
  static async aggregateExportData(filters: ExportDataFilters): Promise<ExportReportData> {
    try {
      console.log('🔄 Starting data aggregation...', filters);
      
      const { start, end } = this.getDateRange(filters);
      console.log('📅 Date range:', { start, end });
      
      // Fetch data based on filters
      const [patients, appointments, payments, discharges] = await Promise.all([
        filters.includePatients ? this.fetchPatientsData(start, end) : Promise.resolve([]),
        filters.includeAppointments ? this.fetchAppointmentsData(start, end) : Promise.resolve([]),
        filters.includeRevenue || filters.includeDue ? this.fetchPaymentsData(start, end) : Promise.resolve([]),
        filters.includeDischarges ? this.fetchDischargesData(start, end) : Promise.resolve([])
      ]);
      
      // Calculate totals - Using latest payment per patient (matching Payments page logic)
      // Total Revenue = sum of paidAmount from all latest payments
      // Total Due = sum of dueAmount from all latest payments
      const totalRevenue = payments.reduce((sum, p) => sum + p.amount, 0); // amount already contains paidAmount
      
      // For due amounts, we need to fetch fresh from Firebase payments with dueAmount
      const paymentsRef = collection(db, 'payments');
      const paymentsSnapshot = await getDocs(paymentsRef);
      
      const latestPaymentsForDue = new Map<string, any>();
      paymentsSnapshot.forEach((doc) => {
        const data = doc.data();
        const patientId = data.patientId || data.userId;
        if (!patientId) return;
        
        const existing = latestPaymentsForDue.get(patientId);
        if (!existing) {
          latestPaymentsForDue.set(patientId, data);
        } else {
          const existingDate = new Date(existing.lastPaymentDate || existing.registrationDate || 0).getTime();
          const currentDate = new Date(data.lastPaymentDate || data.registrationDate || 0).getTime();
          if (currentDate > existingDate) {
            latestPaymentsForDue.set(patientId, data);
          }
        }
      });
      
      const totalDue = Array.from(latestPaymentsForDue.values()).reduce((sum, p) => {
        return sum + (Number(p.dueAmount) || 0);
      }, 0);
      
      console.log('💰 Revenue Calculation (matching Payments page):');
      console.log(`  - Total patients with payments: ${payments.length}`);
      console.log(`  - Total Revenue (sum of paidAmount from latest payments): ₹${totalRevenue}`);
      console.log(`  - Total Due (sum of dueAmount from latest payments): ₹${totalDue}`);
      payments.forEach(p => console.log(`    ✓ ${p.patientName}: ₹${p.amount} paid (${p.status})`));
      
      // Calculate appointment revenue
      const appointmentRevenue = appointments.reduce((sum, a) => sum + a.amount, 0);
      
      console.log('📅 Appointment Revenue Calculation:');
      console.log(`  - Total appointments: ${appointments.length}`);
      console.log(`  - Total appointment revenue: ₹${appointmentRevenue}`);
      appointments.forEach(a => console.log(`    • ${a.patientName} - ${a.serviceName}: ₹${a.amount} (${a.status})`));
      
      const roomOccupancyRate = filters.includeRoomOccupancy 
        ? await this.fetchRoomOccupancyRate() 
        : 0;
      
      // Calculate total paid and due per patient using latest payment card data
      console.log('🔗 Matching payments to patients...');
      patients.forEach(patient => {
        // Find the latest payment for this patient
        const latestPayment = latestPaymentsForDue.get(patient.id);
        
        if (latestPayment) {
          // Use paidAmount and dueAmount from the latest payment card
          patient.totalPaid = Number(latestPayment.paidAmount) || 0;
          patient.totalDue = Number(latestPayment.dueAmount) || 0;
          
          console.log(`  - ${patient.name}: Paid: ₹${patient.totalPaid}, Due: ₹${patient.totalDue} (from latest payment card)`);
        } else {
          patient.totalPaid = 0;
          patient.totalDue = 0;
        }
      });
      
      const reportData: ExportReportData = {
        totalPatients: patients.length,
        totalRevenue,
        totalDue,
        totalAppointments: appointments.length,
        appointmentRevenue,
        roomOccupancyRate,
        totalDischarges: discharges.length,
        patients,
        appointments,
        payments,
        discharges,
        startDate: start,
        endDate: end,
        generatedAt: new Date()
      };
      
      console.log('✅ Data aggregation complete:', {
        totalPatients: reportData.totalPatients,
        totalRevenue: reportData.totalRevenue,
        totalAppointments: reportData.totalAppointments
      });
      
      return reportData;
    } catch (error) {
      console.error('❌ Error aggregating export data:', error);
      throw error;
    }
  }
}

export default ExportDataService;
